import { useDisclosure } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useSnackbar } from 'ui-components';
import { SavedList } from '~/clientApi/savedLists.model';
import { SavedListCreateListModal } from '~/components/SavedListCreateListModal';
import { SavedListsModal } from '~/components/SavedListsModal';
import { SavedListsModalV2 } from '~/components/SavedListsModalV2';
import { SavedListMapState } from '~/components/SavedListsModalV2/SavedListsModalV2.types';
import { queryClient } from '~/config/reactQueryConfig';
import { FEATURE_FLAGS, SNACKBAR_ERROR_GENERIC_OPTIONS } from '~/constants';
import { routes } from '~/core/router';
import { useUpdateSaveListCreators } from '~/hooks/api/useSavedLists';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import {
  AGENCY_SAVED_LISTS,
  SAVED_LISTS,
  SAVED_LIST_CREATORS,
  SAVED_LIST_CREATORS_SEARCH,
} from '~/queries/keys';
import { useSavedListsStore } from '~/screens/MyAgency/store/useSavedListsStore';
import { ICreator } from '~/types';

interface Props {
  creator?: ICreator;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const AddingToAnExistingListModalV1: React.FC<Props> = ({
  creator,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { creators, clearStore } = useSavedListsStore();
  const {
    isOpen: isCreateListModalOpened,
    onClose: onCreateListModalClose,
    onOpen: onCreateListModalOpen,
  } = useDisclosure();
  const router = useRouter();
  const snackbar = useSnackbar();

  const creatorIds = useMemo(
    () => (creator ? [creator.id] : creators.map(({ id }) => id)),
    [creator, creators],
  );

  const { mutateAsync } = useUpdateSaveListCreators();

  const handleCreateNewListModalOpen = useCallback(() => {
    onClose();
    onCreateListModalOpen();
  }, [onClose, onCreateListModalOpen]);

  const handleAddToExistingList = useCallback(
    async (list: SavedList) => {
      try {
        await mutateAsync(
          { id: list.id, addCreatorIds: creatorIds },
          {
            onSuccess: (_data, { id }) => {
              clearStore();

              onSuccess && onSuccess();

              router.push(routes.savedListDetail(id));
            },
          },
        );
        snackbar.success({
          description: 'List updated: The list has been updated',
          duration: 9000,
          isCloseable: true,
        });
      } catch {
        snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
      }
    },
    [mutateAsync, creatorIds, snackbar, clearStore, router, onSuccess],
  );

  const handleSuccess = () => clearStore();

  return (
    <>
      <SavedListCreateListModal
        creatorIds={creatorIds}
        isOpen={isCreateListModalOpened}
        onClose={onCreateListModalClose}
        onSuccess={handleSuccess}
      />

      <SavedListsModal
        isOpen={isOpen}
        onClose={onClose}
        onCreateListOpen={handleCreateNewListModalOpen}
        onItemClick={handleAddToExistingList}
      />
    </>
  );
};

export const AddingToAnExistingListModalV2: React.FC<Props> = ({
  creator,
  isOpen,
  onClose,
}) => {
  const { creators, clearStore } = useSavedListsStore();
  const router = useRouter();
  const snackbar = useSnackbar();

  const creatorList = useMemo(() => {
    if (creators.length) {
      return creators;
    }

    if (creator) {
      return [creator];
    }

    return [];
  }, [creator, creators]);

  const handleSuccess = (state: SavedListMapState) => {
    const listIds = Object.keys(state);

    clearStore();

    queryClient.invalidateQueries([AGENCY_SAVED_LISTS]);
    queryClient.invalidateQueries([SAVED_LIST_CREATORS_SEARCH]);
    queryClient.invalidateQueries([SAVED_LIST_CREATORS]);
    queryClient.invalidateQueries([SAVED_LISTS]);

    if (listIds.length === 1) {
      return snackbar.success({
        action: {
          label: 'View',
          onClick: () => router.push(routes.savedListDetail(listIds[0])),
        },
        description: 'Talent successfully added to list',
        isCloseable: true,
      });
    }

    snackbar.success({
      description: 'Talent successfully added to lists',
      isCloseable: true,
    });
  };

  return (
    <SavedListsModalV2
      creators={creatorList}
      isOpen={isOpen}
      onClose={onClose}
      onSuccess={handleSuccess}
    />
  );
};

export const AddingToAnExistingListModal: React.FC<Props> = (props) => {
  const { isEnabled, status } = useFeature(FEATURE_FLAGS.foamAddToListWithNote);

  if (status === 'loading') {
    return null;
  }

  if (isEnabled) {
    return <AddingToAnExistingListModalV2 {...props} />;
  }

  return <AddingToAnExistingListModalV1 {...props} />;
};
